import React, { useEffect, useState } from "react";
import { Form } from "semantic-ui-react";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	FormControlLabel,
	Checkbox,
	Stepper,
	Step,
	StepButton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useAuth } from "../../context/authContext";
import "./AcceptEULA.css";
import { ASSETS_BASE_URL } from "../../config";
import { ROUTES, TITLES } from "../../constants";
import { useTranslation } from "react-i18next";
import { acceptEula } from "../../services/EULAService";
import { AuthPageStyles } from "../../utilities/themeColors";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useCompliance } from "../../context/complianceContext";
import CompanyLogo from "../../components/CompanyLogo/CompanyLogo";

const normalizeBoolean = (value, fallback = false) => {
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value === 1;
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (normalized === "true" || normalized === "1" || normalized === "yes") return true;
		if (normalized === "false" || normalized === "0" || normalized === "no") return false;
	}
	return fallback;
};

const AcceptEULA = () => {
	const [eulaChecked, setEulaChecked] = useState(false);
	const [eulaRead, setEulaRead] = useState(false);
	const [loading, setLoading] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const { user, updateUserStatus, logout } = useAuth();
	const { items, loading: policyLoading, refresh } = useCompliance();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { authBackdropStyle } = useBrandingAssets();
	const eulas = items.EULA || [];
	const selectedEula = eulas[activeStep] || null;
	const showAcceptanceSection = eulas.length <= 1 || activeStep === eulas.length - 1;

	useEffect(() => {
		refresh();
	}, [refresh]);

	useEffect(() => {
		const handleBackNavigation = async () => {
			await logout();
		};

		window.history.pushState({ acceptEula: true }, "", window.location.href);
		window.addEventListener("popstate", handleBackNavigation);

		return () => {
			window.removeEventListener("popstate", handleBackNavigation);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (activeStep > 0 && activeStep >= eulas.length) {
			setActiveStep(0);
		}
	}, [activeStep, eulas.length]);

	const handleAcceptEULA = async () => {
		setLoading(true);
		try {
			const response = await acceptEula(
				user.user_id,
				eulaChecked,
				eulas.map((doc) => doc.id)
			);
			if (!response?.status) {
				throw new Error(response?.message || t('EULA_ADMIN.FAILED_TO_ACCEPT_EULA'));
			}
			updateUserStatus({ eula_accepted: true, eula_accepted_date: new Date() });
			if (normalizeBoolean(user?.first_login, false) || !normalizeBoolean(user?.email_verified, false)) {
				navigate(ROUTES.VERIFY_EMAIL, { state: { forceOtpSend: true } });
			} else {
				navigate(ROUTES.HOME_ALIAS);
			}
		} catch (error) {
			console.error("Error accepting EULA:", error);
		} finally {
			setLoading(false);
		}
	};

	const buildIframeSrc = (src = "") => {
		const value = String(src || "").trim();
		if (!value) return value;
		const isPdf = /\.pdf($|[?#])/i.test(value);
		if (!isPdf) return value;
		const hasHash = value.includes("#");
		const hashParams = "toolbar=0&navpanes=0&scrollbar=0&view=FitH";
		return `${value}${hasHash ? "&" : "#"}${hashParams}`;
	};

	const getDocumentKind = (src = "") => {
		const value = String(src || "").trim().toLowerCase();
		if (!value) return "unknown";
		if (/\.(png|jpe?g|gif|webp|bmp|svg)($|[?#])/.test(value)) return "image";
		if (/\.pdf($|[?#])/.test(value)) return "pdf";
		return "other";
	};

	const renderEulaContent = () => {
		const frameWrapperSx = {
			height: "100%",
			minHeight: { xs: "260px", sm: "340px", md: "360px", lg: "430px", xl: "560px" },
			border: "1px solid",
			borderColor: "divider",
			borderRadius: 3,
			overflow: "hidden",
			backgroundColor: (theme) => theme.palette.background.paper,
			width: "100%",
			maxWidth: "100%",
			boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
		};

		const renderIframe = (src, title = "EULA") => (
			<Box sx={frameWrapperSx}>
				<iframe
					src={buildIframeSrc(src)}
					width="100%"
					height="100%"
					style={{ border: "none", display: "block" }}
					title={title}
				/>
			</Box>
		);

		const renderImage = (src, title = "EULA") => (
			<Box
				sx={{
					...frameWrapperSx,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: { xs: 1, md: 2 },
					background:
						"linear-gradient(180deg, rgba(248,248,248,1) 0%, rgba(241,241,241,1) 100%)",
				}}
			>
				<Box
					component="img"
					src={src}
					alt={title}
					sx={{
						maxWidth: "100%",
						maxHeight: "100%",
						objectFit: "contain",
						borderRadius: 1,
						boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
						backgroundColor: "#fff",
					}}
				/>
			</Box>
		);

		if (!selectedEula) {
			return renderIframe("https://eula.ithena.io");
		}

		if (selectedEula.file_path) {
			const assetUrl = `${ASSETS_BASE_URL}/${selectedEula.file_path}`;
			const documentKind = getDocumentKind(selectedEula.file_path);
			if (documentKind === "image") {
				return renderImage(assetUrl, selectedEula.title);
			}
			return renderIframe(assetUrl, selectedEula.title);
		}

		if (selectedEula.external_url) {
			return renderIframe(selectedEula.external_url, selectedEula.title || "EULA");
		}

		return (
			<Box
				sx={{
					...frameWrapperSx,
					p: 2,
					overflowY: "auto",
					overflowX: "auto",
				}}
				dangerouslySetInnerHTML={{ __html: selectedEula.description_html || "" }}
			/>
		);
	};

	const renderDocumentDetails = () => {
		if (!selectedEula) return null;
		if (selectedEula.file_path || selectedEula.external_url) return null;
		const descriptionHtml = selectedEula.description_html;
		const descriptionText =
			selectedEula.description ||
			selectedEula.short_description ||
			selectedEula.summary ||
			"";
		return (
			<Box
				sx={{
					mt: 2,
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					p: 2,
					backgroundColor: (theme) => theme.palette.background.paper,
					overflowX: "hidden",
					"& *": { maxWidth: "100%" },
					"& table": { width: "100%", tableLayout: "fixed" },
				}}
			>
				<Typography variant="subtitle1" fontWeight={600}>
					{t('LABELS.DOCUMENT_DETAILS') || "Document Details"}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{t('LABELS.DESCRIPTION') || "Description"}
				</Typography>
				{descriptionHtml ? (
					<Box sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
				) : (
					<Typography variant="body2" sx={{ mt: 1 }}>
						{descriptionText || t('UI_TEXT.NOT_AVAILABLE')}
					</Typography>
				)}
			</Box>
		);
	};

	const renderActionButtons = () => {
		const isMultiStep = eulas.length > 1;
		const isLastStep = activeStep === eulas.length - 1;
		const acceptButton = (
			<Button
				variant="contained"
				color="primary"
				className="button-primary"
				type="submit"
				sx={{
					minWidth: "120px",
					backgroundColor: loading || !eulaChecked || !eulaRead ? "#ddd !important" : "primary.main",
					"&:hover": {
						backgroundColor: loading || !eulaChecked || !eulaRead ? "#ddd !important" : "primary.dark",
					},
				}}
				onClick={handleAcceptEULA}
				disabled={loading || !eulaChecked || !eulaRead}>
				{loading ? t('BUTTON.SUBMITTING') : t('EULA_ADMIN.ACCEPT_EULA_1')}
			</Button>
		);

		if (!isMultiStep) {
			return (
				<Box display="flex" justifyContent="center" mt={2}>
					{acceptButton}
				</Box>
			);
		}

		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					gap: 2,
					mt: 2,
					flexWrap: "wrap",
				}}
			>
				<Button
					variant="outlined"
					onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))}
					disabled={activeStep === 0}
				>
					{t("BUTTON.BACK")}
				</Button>
				{isLastStep && showAcceptanceSection ? (
					acceptButton
				) : (
					<Button
						variant="outlined"
						onClick={() => setActiveStep((prev) => Math.min(eulas.length - 1, prev + 1))}
						disabled={isLastStep}
					>
						{t("BUTTON.NEXT")}
					</Button>
				)}
			</Box>
		);
	};

	const handleBackToLogin = async (e) => {
		e.preventDefault();
		await logout();
	};

	return (
		<Box className="login-container accept-eula-page" sx={authBackdropStyle || AuthPageStyles}>
			<Box
				className="login-form-container accept-eula-card"
				sx={{
					width: { xs: "96vw", sm: "94vw", md: "94vw", lg: "92vw", xl: "1440px" },
					maxWidth: { xs: "96vw", sm: "96vw", md: "1360px", xl: "1440px" },
					height: { xs: "auto", sm: "auto", md: "92vh", lg: "92vh" },
					minHeight: { md: "680px", xl: "800px" },
					overflow: { xs: "visible", md: "hidden" },
					boxSizing: "border-box",
					px: { xs: 2, sm: 2.5, md: 2.25, lg: 2.75, xl: 3.5 },
					pb: { xs: 2, sm: 2.5, md: 1.5, lg: 2, xl: 3 },
					pt: { xs: 2, sm: 2.5, md: 2.5, lg: 3.25, xl: 4 },
					borderRadius: { xs: "18px", md: "24px" },
				}}
			>
				<CompanyLogo alt="Logo" className="login-logo" />
				<Box
					className="form-container accept-eula-form-container"
					sx={{
						pt: { xs: 1, md: 0.25, lg: 0.5, xl: 1 },
						width: "100%",
						height: "100%",
						overflow: "hidden",
						justifyContent: "flex-start",
						alignItems: "stretch",
						rowGap: { xs: 0.25, md: 0, lg: 0.15 },
					}}
				>
					<PageTitle title={t('TITLES.END_USER_LICENSE_AGREEMENT')} />

					<Form
						className="login-form accept-eula-form"
						style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflow: { xs: "visible", md: "hidden" }, display: "flex", flexDirection: "column", rowGap: { xs: 0.5, md: 0.25 } }}>
							{policyLoading ? (
								<Box textAlign="center" py={4}>
									<CircularProgress />
								</Box>
							) : (
								<>
									{eulas.length > 1 && (
										<Box sx={{ width: "100%", pb: 1 }}>
											<Stepper nonLinear activeStep={activeStep} sx={{ mb: 2 }}>
												{eulas.map((doc, index) => (
													<Step key={doc.id} completed={index < activeStep}>
														<StepButton
															color="inherit"
															onClick={() => setActiveStep(index)}
															sx={{
																"& .MuiStepLabel-label": {
																	fontSize: { xs: "0.75rem", sm: "0.875rem" },
																	whiteSpace: "normal",
																	wordBreak: "break-word",
																},
															}}
														>
															{doc.title}
														</StepButton>
													</Step>
												))}
											</Stepper>
										</Box>
									)}
									<Box sx={{ flex: 1, minHeight: 0 }}>{renderEulaContent()}</Box>
									{renderDocumentDetails()}
								</>
							)}
						</Box>
						{showAcceptanceSection && (
							<>
									<Box
										mt={{ xs: 1.25, md: 0.75, lg: 1 }}
										textAlign="center"
										display="flex"
										flexDirection="column"
										gap={{ xs: 0.25, md: 0 }}
										sx={{
											borderTop: "1px solid",
											borderColor: "divider",
											pt: { xs: 1.25, md: 0.75, lg: 1 },
											pb: 0,
											alignItems: "flex-start",
											"& .MuiFormControlLabel-label": {
												fontSize: { xs: "0.95rem", md: "0.82rem", lg: "0.9rem", xl: "0.95rem" },
											},
										}}
									>
									<FormControlLabel
										sx={{ mx: 0 }}
										control={
											<Checkbox
												checked={eulaRead}
												onChange={(e) => setEulaRead(e.target.checked)}
											/>
										}
										label={t('LABELS.EULA_READ_CONFIRM') || "I confirm that I have read the full agreement"}
									/>
									<FormControlLabel
										sx={{ mx: 0 }}
										control={
											<Checkbox
												checked={eulaChecked}
												onChange={(e) => setEulaChecked(e.target.checked)}
											/>
										}
										label={t('LABELS.EULA_AGREEMENT')}
									/>
								</Box>
								<Box mb={0} mt={{ xs: 0.25, md: 0 }}>
									<Typography variant="body2" textAlign="center" color="text.secondary" sx={{ fontSize: { xs: "0.9rem", md: "0.78rem", lg: "0.82rem" }, lineHeight: 1.25 }}>
										{t('UI_TEXT.EULA_INSTRUCTIONS')}
									</Typography>
								</Box>
							</>
						)}
						{renderActionButtons()}
					</Form>
					<Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mt: { xs: 1.5, md: 0.75, lg: 1 } }}>
						<Link to={ROUTES.LOGIN} className="login-link" onClick={handleBackToLogin}>
							{t('UI_TEXT.BACK_TO_LOGIN')}
						</Link>
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default AcceptEULA;
